/**
 * Pyjama PDP: replace product gallery when color/option changes.
 * Custom metafield galleries include images and videos from the metafield list.
 */
(function () {
  const PyjamaVariantGallery = class {
    constructor(mediaGallery) {
      this.gallery = mediaGallery;
      this.section = mediaGallery.closest('.js-product');
      const dataEl = mediaGallery.querySelector('.js-pyjama-variant-gallery-data');
      if (!dataEl) return;

      try {
        this.config = JSON.parse(dataEl.textContent);
      } catch (error) {
        console.warn('Pyjama variant gallery: invalid JSON config', error);
        return;
      }

      this.variantPicker = this.section.querySelector('variant-picker');
      this.mainSlider = mediaGallery.querySelector('.main-image carousel-slider');
      this.mainGrid = mediaGallery.querySelector('.main-image .slider__grid');
      this.thumbGrid = mediaGallery.querySelector('.thumbnails .slider__grid');
      this.collage = mediaGallery.querySelector('.product-media-collage');
      this.overlayTemplate = mediaGallery.querySelector('.js-pyjama-model-overlay-template');
      this.currentGalleryKey = null;

      this.originalMarkup = {
        main: this.mainGrid ? this.mainGrid.innerHTML : '',
        thumbs: this.thumbGrid ? this.thumbGrid.innerHTML : '',
        collage: this.collage ? this.collage.innerHTML : ''
      };
      const zoomTemplate = mediaGallery.querySelector('.js-media-zoom-template');
      const zoomThumbs = zoomTemplate?.content?.querySelector('.gallery-viewer__thumbs');
      this.originalGalleryViewerThumbs = zoomThumbs ? zoomThumbs.innerHTML : '';
      this.thumbClickHandler = null;

      this.section.addEventListener('on:variant:change', (evt) => {
        const galleryKey = this.getGalleryKey(evt.detail.selectedOptions);
        const previousKey = this.currentGalleryKey;
        this.applyGallery(galleryKey);
        this.updateModelOverlay(evt.detail.selectedOptions);

        // Only scroll when the color/gallery changes — not on size-only selection.
        if (galleryKey !== previousKey) {
          window.requestAnimationFrame(() => {
            this.scrollToMainImage();
          });
        }
      });

      this.bindColorOptionListeners();

      if (this.variantPicker) {
        theme.whenComponentLoaded([this.variantPicker], () => {
          const selectedOptions = this.variantPicker.getSelectedOptions();
          const galleryKey = this.getGalleryKey(selectedOptions);
          if (this.hasCustomGallery(galleryKey)) {
            this.applyGallery(galleryKey, true);
          }
          this.updateModelOverlay(selectedOptions);
        });
      }
    }

    getModelOverlayText(selectedOptions) {
      if (this.config.optionIndex != null && selectedOptions) {
        const color = selectedOptions[this.config.optionIndex];
        const fromColor = color && this.config.modelOverlays ? this.config.modelOverlays[color] : null;
        if (fromColor) return fromColor;
      }
      return this.config.modelOverlayDefault || '';
    }

    buildModelOverlayHtml(text) {
      if (!text) return '';
      const safe = String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>');
      return `<div class="product-media__model-overlay"><span class="product-media__model-overlay-text">${safe}</span></div>`;
    }

    updateModelOverlay(selectedOptions) {
      const text = this.getModelOverlayText(selectedOptions);
      const nodes = this.gallery.querySelectorAll('.product-media__model-overlay-text');
      if (nodes.length) {
        nodes.forEach((el) => {
          el.textContent = text || '';
        });
        nodes.forEach((el) => {
          const wrap = el.closest('.product-media__model-overlay');
          if (wrap) wrap.hidden = !text;
        });
        return;
      }
      if (!text) return;
      const firstMedia = this.gallery.querySelector('.main-image .slider__item.is-active .product-media--image, .product-media-collage__item.is-active .product-media--image, .main-image .product-media--image');
      if (firstMedia && !firstMedia.querySelector('.product-media__model-overlay')) {
        firstMedia.insertAdjacentHTML('beforeend', this.buildModelOverlayHtml(text));
      }
    }

    bindColorOptionListeners() {
      if (this.config.optionIndex == null || !this.variantPicker) return;

      this.variantPicker.addEventListener('change', (evt) => {
        const input = evt.target;
        if (!input.matches('input.opt-btn')) return;

        const selector = input.closest('.option-selector');
        const selectorIndex = selector ? Number(selector.dataset.index) - 1 : -1;
        if (selectorIndex !== this.config.optionIndex) return;

        window.requestAnimationFrame(() => {
          const selectedOptions = this.variantPicker.getSelectedOptions();
          const galleryKey = this.getGalleryKey(selectedOptions);
          this.applyGallery(galleryKey);
          this.updateModelOverlay(selectedOptions);
        });
      });
    }

    getGalleryKey(selectedOptions) {
      if (this.config.optionIndex == null || !selectedOptions) {
        return 'default';
      }

      const value = selectedOptions[this.config.optionIndex];
      const idx = this.config.optionValues.indexOf(value);
      if (idx === -1) return 'default';

      const key = String(idx + 1);
      if (this.hasCustomGallery(key)) return key;
      return 'default';
    }

    hasCustomGallery(galleryKey) {
      const gallery = this.config.galleries[galleryKey];
      return Boolean(gallery && gallery.length);
    }

    applyGallery(galleryKey, firstLoad = false) {
      const sameGallery = this.currentGalleryKey === galleryKey && !firstLoad;
      if (sameGallery) return;

      window.pauseAllMedia();

      if (galleryKey === 'default') {
        this.restoreOriginalGallery();
      } else {
        const images = this.config.galleries[galleryKey];
        if (!images || !images.length) return;
        this.renderCustomGallery(images);
      }

      this.currentGalleryKey = galleryKey;
      this.ensureVideoAssets();
      this.finishGalleryUpdate(firstLoad);
    }

    ensureVideoAssets() {
      if (!this.mainGrid?.querySelector('deferred-media, video-component.has-video')) return;

      if (this.config.videoCss && !document.querySelector('link[href*="video.css"]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = this.config.videoCss;
        document.head.appendChild(link);
      }

      if (this.config.videoJs && !document.querySelector('script[src*="video.js"]')) {
        const script = document.createElement('script');
        script.src = this.config.videoJs;
        script.defer = true;
        document.head.appendChild(script);
      }
    }

    restoreOriginalGallery() {
      if (this.mainGrid && this.originalMarkup.main) {
        this.mainGrid.innerHTML = this.originalMarkup.main;
      }
      if (this.thumbGrid && this.originalMarkup.thumbs) {
        this.thumbGrid.innerHTML = this.originalMarkup.thumbs;
      }
      if (this.collage && this.originalMarkup.collage) {
        this.collage.innerHTML = this.originalMarkup.collage;
      }
    }

    renderCustomGallery(items) {
      if (this.mainGrid) {
        this.mainGrid.innerHTML = items.map((item) => {
          if (item.mainSlide) return item.mainSlide;
          return this.buildMainSlide(item, 0, items.length);
        }).join('');
      }

      if (this.thumbGrid) {
        this.thumbGrid.innerHTML = items.map((item, index) => {
          if (item.thumbSlide) return item.thumbSlide;
          return this.buildThumbnail(item, index);
        }).join('');
      }

      if (this.collage) {
        this.collage.innerHTML = items.map((item, index) => {
          if (item.collageSlide) return item.collageSlide;
          return this.buildCollageItem(item, index);
        }).join('');
      }
    }

    finishGalleryUpdate(firstLoad) {
      this.mainSlider = this.gallery.querySelector('.main-image carousel-slider');
      this.mainGrid = this.gallery.querySelector('.main-image .slider__grid');
      this.thumbGrid = this.gallery.querySelector('.thumbnails .slider__grid');
      this.collage = this.gallery.querySelector('.product-media-collage');

      if (typeof this.gallery.refreshMediaLists === 'function') {
        this.gallery.refreshMediaLists();
      }

      const carousels = [
        this.mainSlider,
        this.thumbGrid ? this.thumbGrid.closest('carousel-slider') : null
      ];
      carousels.forEach((carousel) => {
        if (carousel && typeof carousel.refresh === 'function') {
          carousel.refresh();
        }
      });

      const firstItem = this.mainGrid && this.mainGrid.querySelector('.slider__item[data-media-id]');
      if (firstItem && typeof this.gallery.setActiveMedia === 'function') {
        this.gallery.setActiveMedia(firstItem.dataset.mediaId, !firstLoad, firstLoad);
      }

      if (typeof this.gallery.playThumbnailVideos === 'function') {
        this.gallery.playThumbnailVideos();
        window.setTimeout(() => {
          this.gallery.playThumbnailVideos();
        }, 150);
      }

      this.syncGalleryViewerThumbs();
      this.bindThumbnailClicks();
    }

    bindThumbnailClicks() {
      if (!this.thumbGrid) return;

      if (this.thumbClickHandler) {
        this.thumbGrid.removeEventListener('click', this.thumbClickHandler);
      }

      this.thumbClickHandler = (evt) => {
        const thumb = evt.target.closest('.thumbnail');
        if (!thumb) return;

        const item = thumb.closest('.slider__item');
        if (!item?.dataset.mediaId) return;

        evt.preventDefault();
        const mediaId = item.dataset.mediaId;

        if (typeof this.gallery.setActiveMedia === 'function') {
          this.gallery.setActiveMedia(mediaId, false);
        }

        this.scrollMainCarouselToMedia(mediaId);
      };

      this.thumbGrid.addEventListener('click', this.thumbClickHandler);
    }

    scrollMainCarouselToMedia(mediaId) {
      const mediaEl = this.mainGrid?.querySelector(`.slider__item[data-media-id="${CSS.escape(String(mediaId))}"]`);
      if (!mediaEl) return;

      if (this.mainSlider && typeof this.mainSlider.refresh === 'function') {
        this.mainSlider.refresh();
      }

      const slider = this.mainSlider?.querySelector('.slider');
      if (slider) {
        slider.scrollTo({ left: mediaEl.offsetLeft, behavior: 'instant' });
      } else if (this.mainSlider?.scrollToElement) {
        this.mainSlider.scrollToElement(mediaEl, 'instant');
      }

      this.mainGrid?.querySelectorAll('.slider__item').forEach((slide) => {
        const isActive = slide.dataset.mediaId === String(mediaId);
        slide.classList.toggle('is-active', isActive);
        if (isActive) {
          slide.removeAttribute('tabindex');
        } else {
          slide.setAttribute('tabindex', '-1');
        }
      });

      this.thumbGrid?.querySelectorAll('.slider__item').forEach((thumb) => {
        thumb.classList.toggle('is-active', thumb.dataset.mediaId === String(mediaId));
      });
    }

    syncGalleryViewerThumbs() {
      if (!this.gallery.hasAttribute('data-zoom-enabled')) return;

      const thumbsHtml = this.currentGalleryKey === 'default'
        ? this.originalGalleryViewerThumbs
        : this.buildGalleryViewerThumbsHtml();

      if (!thumbsHtml) return;

      const templateThumbs = this.gallery.querySelector('.js-media-zoom-template')?.content?.querySelector('.gallery-viewer__thumbs');
      if (templateThumbs) {
        templateThumbs.innerHTML = thumbsHtml;
      }

      document.querySelectorAll('.gallery-viewer-modal .gallery-viewer__thumbs').forEach((container) => {
        container.innerHTML = thumbsHtml;
      });
    }

    buildGalleryViewerThumbsHtml() {
      if (!this.mainGrid) return '';

      return [...this.mainGrid.querySelectorAll('.slider__item')].flatMap((slide) => {
        const link = slide.querySelector('a.show-gallery');
        if (!link) return [];

        const img = link.querySelector('img');
        const zoomUrl = link.getAttribute('href');
        if (!zoomUrl || !img) return [];

        const thumbSrc = img.currentSrc || img.getAttribute('src') || '';
        const mediaId = slide.dataset.mediaId || '';

        return [`
          <a class="gallery-viewer__thumb" href="#" data-zoom-url="${this.escapeAttr(zoomUrl)}" data-media-id="${this.escapeAttr(mediaId)}">
            <img class="gallery-viewer__thumb-img" src="${this.escapeAttr(thumbSrc)}" alt="${this.escapeAttr(img.alt || '')}" loading="lazy">
          </a>
        `];
      }).join('');
    }

    /**
     * Scroll so the main gallery image sits fully below the sticky header.
     */
    scrollToMainImage() {
      const mainImage = this.gallery.querySelector('.main-image');
      if (!mainImage) return;

      const elTop = typeof theme.getOffsetTopFromDoc === 'function'
        ? theme.getOffsetTopFromDoc(mainImage)
        : window.scrollY + mainImage.getBoundingClientRect().top;
      const headerOffset = typeof theme.stickyHeaderHeight === 'function'
        ? theme.stickyHeaderHeight()
        : 0;

      window.scrollTo({ top: Math.max(0, elTop - headerOffset - 12), behavior: 'smooth' });
    }

    buildMainSlide(img, index, total) {
      const isActive = index === 0;
      const zoomEnabled = this.config.enableZoom;
      const selectedOptions = this.variantPicker ? this.variantPicker.getSelectedOptions() : null;
      const overlayHtml = index === 0
        ? this.buildModelOverlayHtml(this.getModelOverlayText(selectedOptions))
        : '';

      const loadingAttrs = isActive
        ? 'loading="eager" fetchpriority="high"'
        : 'loading="lazy"';

      let inner = `
        <div class="product-media product-media--image">
          <img
            src="${this.escapeAttr(img.src)}"
            srcset="${this.escapeAttr(img.srcset)}"
            sizes="${this.escapeAttr(this.config.imageSizes)}"
            width="${img.width}"
            height="${img.height}"
            alt="${this.escapeAttr(img.alt)}"
            class="theme-img"
            ${loadingAttrs}
            style="--aspect-ratio: ${img.aspectRatio};"
          >
          ${overlayHtml}
        </div>`;

      if (zoomEnabled) {
        inner = `<a class="block show-gallery" href="${this.escapeAttr(img.zoomUrl)}" aria-label="Bild ${index + 1}">${inner}</a>`;
      }

      return `
        <div class="slider__item${isActive ? ' is-active' : ''}"
          role="group"
          aria-label="${index + 1} von ${total}"
          ${isActive ? '' : ' tabindex="-1"'}
          data-media-id="${this.escapeAttr(img.id)}">
          ${inner}
        </div>`;
    }

    buildThumbnail(img, index) {
      const isActive = index === 0;

      return `
        <li class="slider__item${isActive ? ' is-active' : ''}" data-media-id="${this.escapeAttr(img.id)}">
          <a class="thumbnail thumbnail--media-image" href="#">
            <img
              src="${this.escapeAttr(img.thumbSrc || img.src)}"
              srcset="${this.escapeAttr(img.thumbSrcset || img.srcset)}"
              sizes="65px"
              width="${img.width}"
              height="${img.height}"
              alt="${this.escapeAttr(img.alt)}"
              class="theme-img"
              loading="lazy"
              style="--aspect-ratio: ${img.aspectRatio};"
            >
          </a>
        </li>`;
    }

    buildCollageItem(img, index) {
      const isActive = index === 0;
      const zoomEnabled = this.config.enableZoom;
      const selectedOptions = this.variantPicker ? this.variantPicker.getSelectedOptions() : null;
      const overlayHtml = index === 0
        ? this.buildModelOverlayHtml(this.getModelOverlayText(selectedOptions))
        : '';

      const loadingAttrs = index < 4 ? 'loading="eager"' : 'loading="lazy"';

      let inner = `
        <div class="product-media product-media--image">
          <img
            src="${this.escapeAttr(img.src)}"
            srcset="${this.escapeAttr(img.srcset)}"
            sizes="${this.escapeAttr(this.config.imageSizes)}"
            width="${img.width}"
            height="${img.height}"
            alt="${this.escapeAttr(img.alt)}"
            class="theme-img"
            ${loadingAttrs}
            style="--aspect-ratio: ${img.aspectRatio};"
          >
          ${overlayHtml}
        </div>`;

      if (zoomEnabled) {
        inner = `<a class="show-gallery" href="${this.escapeAttr(img.zoomUrl)}">${inner}</a>`;
      }

      return `
        <div class="product-media-collage__item${isActive ? ' is-active' : ''}" data-media-id="${this.escapeAttr(img.id)}">
          ${inner}
        </div>`;
    }

    escapeAttr(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;');
    }
  };

  function initPyjamaVariantGalleries(root = document) {
    root.querySelectorAll('media-gallery[data-pyjama-variant-galleries]').forEach((el) => {
      if (!el.pyjamaVariantGallery) {
        el.pyjamaVariantGallery = new PyjamaVariantGallery(el);
      }
    });
  }

  window.initPyjamaVariantGalleries = initPyjamaVariantGalleries;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initPyjamaVariantGalleries());
  } else {
    initPyjamaVariantGalleries();
  }
})();
